import { z } from 'zod';

/**
 * Valida formato de email com regras robustas
 * @param email - string a ser validada
 * @returns boolean indicando se é válido
 */
export const isValidEmail = (email: string): boolean => {
  if (!email || email.trim() === '') return false;
  
  // Regex robusta para email
  const emailRegex = /^[a-zA-Z0-9]([a-zA-Z0-9._-]*[a-zA-Z0-9])?@[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z]{2,})+$/;
  
  // Validações adicionais
  const trimmed = email.trim().toLowerCase();
  
  // Não permite espaços
  if (trimmed.includes(' ')) return false;
  
  // Não permite pontos consecutivos
  if (trimmed.includes('..')) return false;
  
  // Não permite @ múltiplos
  if ((trimmed.match(/@/g) || []).length !== 1) return false;
  
  // Valida tamanho máximo (254 caracteres é o limite RFC)
  if (trimmed.length > 254) return false;
  
  // Valida partes antes e depois do @
  const [localPart, domain] = trimmed.split('@');
  if (!localPart || localPart.length > 64) return false;
  if (!domain || domain.length > 253) return false;
  
  return emailRegex.test(trimmed);
};

/**
 * Normaliza email para formato padrão
 * Remove espaços e converte para minúsculas
 */
export const normalizeEmail = (email: string): string => {
  return email.trim().toLowerCase();
};

/**
 * Schema Zod reutilizável para email obrigatório
 */
export const emailRequiredSchema = z.string()
  .min(1, { message: 'Email é obrigatório' })
  .email({ message: 'Email inválido' })
  .max(254, { message: 'Email muito longo' })
  .transform(normalizeEmail)
  .refine(isValidEmail, { message: 'Formato de email inválido' });

/**
 * Schema Zod reutilizável para email opcional
 */
export const emailOptionalSchema = z.string()
  .optional()
  .or(z.literal(''))
  .refine(
    (val) => {
      if (!val || val === '') return true;
      return isValidEmail(val);
    },
    { message: 'Formato de email inválido' }
  )
  .transform((val) => val ? normalizeEmail(val) : val);
